import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  inviteMember,
  isProRequiredError,
  listMembers,
  revokeMember,
  type ProjectMember,
  type SharePermission,
} from "../lib/api";

export function ProjectMembers() {
  const { slug } = useParams<{ slug: string }>();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["members", slug],
    queryFn: () => listMembers(slug!),
    enabled: !!slug,
    retry: false,
  });

  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState<SharePermission>("read");
  const [toolSlug, setToolSlug] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      inviteMember(slug!, {
        email: email.trim(),
        permission,
        toolSlug: toolSlug.trim() || undefined,
      }),
    onSuccess: () => {
      setOk(`Invited ${email}`);
      setErr(null);
      setEmail("");
      setToolSlug("");
      qc.invalidateQueries({ queryKey: ["members", slug] });
    },
    onError: (e: unknown) => {
      setOk(null);
      if (isProRequiredError(e)) {
        setErr("Project sharing is a Pro feature. Upgrade in Settings.");
      } else {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
  });

  const revoke = useMutation({
    mutationFn: (m: ProjectMember) =>
      revokeMember(slug!, m.inviteeEmail, m.toolSlug ?? undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", slug] }),
  });

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "32px 28px" }}>
      <Link
        to={`/projects/${slug}`}
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: "var(--fg-3)",
          textDecoration: "none",
        }}
      >
        ← back to {slug}
      </Link>
      <h1
        style={{
          marginTop: 12,
          fontFamily: "var(--font-sans)",
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: "-0.02em",
        }}
      >
        Members
      </h1>
      <p style={{ color: "var(--fg-3)", fontFamily: "var(--font-sans)", fontSize: 14, margin: "8px 0 24px" }}>
        Invite people to view or edit this project. Pro feature.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) return;
          invite.mutate();
        }}
        style={{
          background: "var(--surface-1)",
          border: "1px solid var(--line-2)",
          borderRadius: 10,
          padding: 20,
          marginBottom: 24,
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 10,
          rowGap: 12,
        }}
      >
        <input
          type="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={inputStyle}
        />
        <select
          value={permission}
          onChange={(e) => setPermission(e.target.value as SharePermission)}
          style={{ ...inputStyle, width: 110 }}
        >
          <option value="read">read</option>
          <option value="write">write</option>
        </select>
        <input
          type="text"
          placeholder="optional: scope to one widget by toolSlug"
          value={toolSlug}
          onChange={(e) => setToolSlug(e.target.value)}
          style={inputStyle}
        />
        <button
          type="submit"
          disabled={invite.isPending}
          style={{
            background: "var(--accent)",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 6,
            border: 0,
            cursor: invite.isPending ? "wait" : "pointer",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            fontWeight: 500,
            opacity: invite.isPending ? 0.6 : 1,
          }}
        >
          {invite.isPending ? "Inviting…" : "Invite"}
        </button>
        {err && (
          <div
            style={{
              gridColumn: "1 / -1",
              color: "#F26D6D",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
            }}
          >
            {err}
          </div>
        )}
        {ok && (
          <div
            style={{
              gridColumn: "1 / -1",
              color: "var(--accent-fg)",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
            }}
          >
            {ok}
          </div>
        )}
      </form>

      <h2
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11.5,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: "0 0 12px",
        }}
      >
        Current members
      </h2>
      {list.isLoading && (
        <div style={{ color: "var(--fg-3)", fontSize: 13, fontFamily: "var(--font-mono)" }}>Loading…</div>
      )}
      {list.data && list.data.members.length === 0 && (
        <div
          style={{
            padding: 20,
            border: "1px dashed var(--line-2)",
            borderRadius: 10,
            textAlign: "center",
            color: "var(--fg-3)",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
          }}
        >
          No members yet. Invite someone above.
        </div>
      )}
      {list.data && list.data.members.length > 0 && (
        <div
          style={{
            border: "1px solid var(--line-2)",
            borderRadius: 10,
            overflow: "hidden",
            background: "var(--surface-1)",
          }}
        >
          {list.data.members.map((m, i) => (
            <MemberRow
              key={`${m.inviteeEmail}|${m.toolSlug ?? ""}`}
              m={m}
              first={i === 0}
              onRevoke={() => revoke.mutate(m)}
              revoking={revoke.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MemberRow({
  m,
  first,
  onRevoke,
  revoking,
}: {
  m: ProjectMember;
  first: boolean;
  onRevoke: () => void;
  revoking: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto auto",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        borderTop: first ? "0" : "1px solid var(--line-1)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            color: "var(--fg-1)",
            wordBreak: "break-all",
          }}
        >
          {m.inviteeEmail}
        </div>
        {m.toolSlug && (
          <div
            style={{
              marginTop: 2,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--fg-4)",
            }}
          >
            widget: {m.toolSlug}
          </div>
        )}
      </div>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          padding: "1px 6px",
          border: "1px solid var(--line-2)",
          borderRadius: 999,
          color: m.permission === "write" ? "var(--accent-fg)" : "var(--fg-3)",
        }}
      >
        {m.permission}
      </span>
      <span
        title={
          m.accepted && m.lastLoginAt
            ? `Last signed in ${new Date(m.lastLoginAt).toLocaleString()}`
            : "Has not signed in yet"
        }
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: m.accepted ? "var(--accent-fg)" : "var(--fg-4)",
        }}
      >
        {m.accepted ? "Active" : "Invite pending"}
      </span>
      <button
        onClick={onRevoke}
        disabled={revoking}
        style={{
          background: "transparent",
          color: "var(--fg-3)",
          border: "1px solid var(--line-2)",
          borderRadius: 6,
          padding: "5px 10px",
          fontFamily: "var(--font-sans)",
          fontSize: 12,
          cursor: revoking ? "wait" : "pointer",
        }}
      >
        Revoke
      </button>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--surface-2)",
  color: "var(--fg-1)",
  border: "1px solid var(--line-2)",
  borderRadius: 6,
  padding: "9px 12px",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
};
